import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bankAccountAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';

function BankAccountManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    accountName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    branch: '',
    upiId: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      showToast('Access denied. Admin only.', 'error');
      navigate('/user');
    }
  }, [user, navigate, showToast]);

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  const fetchBankAccounts = async () => {
    setLoading(true);
    try {
      const response = await bankAccountAPI.getAllBankAccounts();
      setBankAccounts(response.data.bankAccounts);
    } catch (error) {
      console.error('Failed to fetch bank accounts:', error);
      showToast('Failed to fetch bank accounts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.accountName || !formData.bankName || !formData.accountNumber || 
        !formData.ifscCode || !formData.branch) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingAccount) {
        await bankAccountAPI.updateBankAccount(editingAccount._id, formData);
        showToast('Bank account updated successfully', 'success');
      } else {
        await bankAccountAPI.createBankAccount(formData);
        showToast('Bank account created successfully', 'success');
      }
      
      resetForm();
      fetchBankAccounts();
    } catch (error) {
      console.error('Error saving bank account:', error);
      showToast(error.response?.data?.message || 'Failed to save bank account', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      accountName: account.accountName,
      bankName: account.bankName,
      accountNumber: account.accountNumber,
      ifscCode: account.ifscCode,
      branch: account.branch,
      upiId: account.upiId || '',
      notes: account.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) {
      return;
    }

    try {
      await bankAccountAPI.deleteBankAccount(id);
      showToast('Bank account deleted successfully', 'success');
      fetchBankAccounts();
    } catch (error) {
      console.error('Error deleting bank account:', error);
      showToast(error.response?.data?.message || 'Failed to delete bank account', 'error');
    }
  };

  const handleToggleActive = async (account) => {
    try {
      await bankAccountAPI.updateBankAccount(account._id, {
        isActive: !account.isActive
      });
      showToast(`Bank account ${!account.isActive ? 'activated' : 'deactivated'} successfully`, 'success');
      fetchBankAccounts();
    } catch (error) {
      console.error('Error toggling bank account status:', error);
      showToast('Failed to update bank account status', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      accountName: '',
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      branch: '',
      upiId: '',
      notes: ''
    });
    setEditingAccount(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '90vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <p style={{ fontSize: '18px', color: '#718096' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '90vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div>
              <h1 style={{ 
                fontSize: '28px', 
                fontWeight: '700',
                color: '#2d3748',
                marginBottom: '8px'
              }}>
                🏦 Bank Account Management
              </h1>
              <p style={{ fontSize: '14px', color: '#718096' }}>
                Manage payment account details for student fee payments
              </p>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                background: showForm ? '#e53e3e' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
            >
              {showForm ? '✕ Cancel' : '+ Add Bank Account'}
            </button>
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <div style={{ 
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '600',
              color: '#2d3748',
              marginBottom: '20px'
            }}>
              {editingAccount ? 'Edit Bank Account' : 'Add New Bank Account'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px'
              }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    color: '#4a5568',
                    marginBottom: '8px'
                  }}>
                    Account Name <span style={{ color: '#e53e3e' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="accountName"
                    value={formData.accountName}
                    onChange={handleInputChange}
                    placeholder="e.g., John Doe or Company Name"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    color: '#4a5568',
                    marginBottom: '8px'
                  }}>
                    Bank Name <span style={{ color: '#e53e3e' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleInputChange}
                    placeholder="e.g., State Bank of India"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      outline: 'none'
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    color: '#4a5568',
                    marginBottom: '8px'
                  }}>
                    Account Number <span style={{ color: '#e53e3e' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleInputChange}
                    placeholder="e.g., 1234567890"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      outline: 'none',
                      fontFamily: 'monospace'
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    color: '#4a5568',
                    marginBottom: '8px'
                  }}>
                    IFSC Code <span style={{ color: '#e53e3e' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="ifscCode"
                    value={formData.ifscCode}
                    onChange={handleInputChange}
                    placeholder="e.g., SBIN0001234"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      outline: 'none',
                      fontFamily: 'monospace',
                      textTransform: 'uppercase'
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    color: '#4a5568',
                    marginBottom: '8px'
                  }}>
                    Branch <span style={{ color: '#e53e3e' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="branch"
                    value={formData.branch}
                    onChange={handleInputChange}
                    placeholder="e.g., Main Branch, Mumbai"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      outline: 'none'
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    color: '#4a5568',
                    marginBottom: '8px'
                  }}>
                    UPI ID
                  </label>
                  <input
                    type="text"
                    name="upiId"
                    value={formData.upiId}
                    onChange={handleInputChange}
                    placeholder="e.g., example@upi"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      outline: 'none',
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginTop: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Additional notes or instructions..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '12px',
                marginTop: '24px'
              }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '12px 32px',
                    fontSize: '14px',
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.7 : 1
                  }}
                >
                  {saving ? 'Saving...' : (editingAccount ? 'Update Account' : 'Create Account')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '12px 32px',
                    fontSize: '14px',
                    fontWeight: '600',
                    background: '#e2e8f0',
                    color: '#4a5568',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Bank Accounts List */}
        <div style={{ 
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600',
            color: '#2d3748',
            marginBottom: '20px'
          }}>
            Bank Accounts ({bankAccounts.length})
          </h2>

          {bankAccounts.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#718096'
            }}>
              <p style={{ fontSize: '48px', marginBottom: '16px' }}>🏦</p>
              <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                No bank accounts added yet
              </p>
              <p style={{ fontSize: '14px' }}>
                Click "Add Bank Account" to create your first account
              </p>
            </div>
          ) : (
            <div style={{ 
              display: 'grid',
              gap: '16px'
            }}>
              {bankAccounts.map((account) => (
                <div 
                  key={account._id}
                  style={{
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '20px',
                    position: 'relative',
                    background: account.isActive ? 'white' : '#f7fafc'
                  }}
                >
                  {/* Status Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px'
                  }}>
                    <span style={{
                      padding: '4px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      borderRadius: '12px',
                      background: account.isActive ? '#d1fae5' : '#fee2e2',
                      color: account.isActive ? '#065f46' : '#991b1b'
                    }}>
                      {account.isActive ? '✓ Active' : '✕ Inactive'}
                    </span>
                  </div>

                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginBottom: '16px'
                  }}>
                    <div>
                      <p style={{ 
                        fontSize: '12px', 
                        color: '#718096',
                        marginBottom: '4px',
                        fontWeight: '600'
                      }}>
                        Account Name
                      </p>
                      <p style={{ 
                        fontSize: '15px', 
                        color: '#2d3748',
                        fontWeight: '600'
                      }}>
                        {account.accountName}
                      </p>
                    </div>

                    <div>
                      <p style={{ 
                        fontSize: '12px', 
                        color: '#718096',
                        marginBottom: '4px',
                        fontWeight: '600'
                      }}>
                        Bank Name
                      </p>
                      <p style={{ 
                        fontSize: '15px', 
                        color: '#2d3748'
                      }}>
                        {account.bankName}
                      </p>
                    </div>

                    <div>
                      <p style={{ 
                        fontSize: '12px', 
                        color: '#718096',
                        marginBottom: '4px',
                        fontWeight: '600'
                      }}>
                        Account Number
                      </p>
                      <p style={{ 
                        fontSize: '15px', 
                        color: '#2d3748',
                        fontFamily: 'monospace',
                        fontWeight: '600'
                      }}>
                        {account.accountNumber}
                      </p>
                    </div>

                    <div>
                      <p style={{ 
                        fontSize: '12px', 
                        color: '#718096',
                        marginBottom: '4px',
                        fontWeight: '600'
                      }}>
                        IFSC Code
                      </p>
                      <p style={{ 
                        fontSize: '15px', 
                        color: '#2d3748',
                        fontFamily: 'monospace',
                        fontWeight: '600'
                      }}>
                        {account.ifscCode}
                      </p>
                    </div>

                    <div>
                      <p style={{ 
                        fontSize: '12px', 
                        color: '#718096',
                        marginBottom: '4px',
                        fontWeight: '600'
                      }}>
                        Branch
                      </p>
                      <p style={{ 
                        fontSize: '15px', 
                        color: '#2d3748'
                      }}>
                        {account.branch}
                      </p>
                    </div>

                    {account.upiId && (
                      <div>
                        <p style={{ 
                          fontSize: '12px', 
                          color: '#718096',
                          marginBottom: '4px',
                          fontWeight: '600'
                        }}>
                          UPI ID
                        </p>
                        <p style={{ 
                          fontSize: '15px', 
                          color: '#667eea',
                          fontFamily: 'monospace',
                          fontWeight: '600'
                        }}>
                          {account.upiId}
                        </p>
                      </div>
                    )}
                  </div>

                  {account.notes && (
                    <div style={{ 
                      marginBottom: '16px',
                      padding: '12px',
                      background: '#f7fafc',
                      borderRadius: '8px'
                    }}>
                      <p style={{ 
                        fontSize: '12px', 
                        color: '#718096',
                        marginBottom: '4px',
                        fontWeight: '600'
                      }}>
                        Notes
                      </p>
                      <p style={{ 
                        fontSize: '14px', 
                        color: '#4a5568'
                      }}>
                        {account.notes}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px',
                    paddingTop: '16px',
                    borderTop: '1px solid #e2e8f0',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      onClick={() => navigate(`/admin/bank-accounts/${account._id}`)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      📊 View Transactions
                    </button>
                    <button
                      onClick={() => handleEdit(account)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        background: '#eef2ff',
                        color: '#667eea',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(account)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        background: account.isActive ? '#fee2e2' : '#d1fae5',
                        color: account.isActive ? '#991b1b' : '#065f46',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      {account.isActive ? '⏸ Deactivate' : '▶ Activate'}
                    </button>
                    <button
                      onClick={() => handleDelete(account._id)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        background: '#fee2e2',
                        color: '#991b1b',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        marginLeft: 'auto'
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BankAccountManagement;
